import { useState, useEffect, useCallback } from 'react';

export interface TrialStatus {
  isTrialActive: boolean;
  isTrialExpired: boolean;
  currentDay: number; // 1〜5
  totalDays: number;
  messagesUsedToday: number;
  messagesLimitPerDay: number;
  messagesRemainingToday: number;
  totalMessagesUsed: number;
  trialStartDate: string | null;
  todayDate: string;
}

const TRIAL_KEY = 'ai_inochan_trial';
const MESSAGES_PER_DAY = 30;
const TRIAL_DAYS = 5;

interface TrialData {
  startDate: string;
  dailyUsage: Record<string, number>; // date -> count
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function getDayNumber(startDate: string): number {
  const start = new Date(startDate);
  const today = new Date(getTodayStr());
  const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return Math.min(diff + 1, TRIAL_DAYS + 1);
}

function loadTrialData(): TrialData | null {
  try {
    const raw = localStorage.getItem(TRIAL_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TrialData;
  } catch {
    return null;
  }
}

function saveTrialData(data: TrialData): void {
  localStorage.setItem(TRIAL_KEY, JSON.stringify(data));
}

export function useTrialSystem() {
  const [trialData, setTrialData] = useState<TrialData | null>(() => loadTrialData());

  const initTrial = useCallback(() => {
    const today = getTodayStr();
    const newData: TrialData = {
      startDate: today,
      dailyUsage: { [today]: 0 },
    };
    saveTrialData(newData);
    setTrialData(newData);
    return newData;
  }, []);

  const getStatus = useCallback((): TrialStatus => {
    const today = getTodayStr();
    if (!trialData) {
      return {
        isTrialActive: false,
        isTrialExpired: false,
        currentDay: 0,
        totalDays: TRIAL_DAYS,
        messagesUsedToday: 0,
        messagesLimitPerDay: MESSAGES_PER_DAY,
        messagesRemainingToday: MESSAGES_PER_DAY,
        totalMessagesUsed: 0,
        trialStartDate: null,
        todayDate: today,
      };
    }

    const dayNumber = getDayNumber(trialData.startDate);
    const isExpired = dayNumber > TRIAL_DAYS;
    const usedToday = trialData.dailyUsage[today] ?? 0;
    const remaining = Math.max(0, MESSAGES_PER_DAY - usedToday);
    const totalUsed = Object.values(trialData.dailyUsage).reduce((a, b) => a + b, 0);

    return {
      isTrialActive: !isExpired && remaining > 0,
      isTrialExpired: isExpired,
      currentDay: Math.min(dayNumber, TRIAL_DAYS),
      totalDays: TRIAL_DAYS,
      messagesUsedToday: usedToday,
      messagesLimitPerDay: MESSAGES_PER_DAY,
      messagesRemainingToday: remaining,
      totalMessagesUsed: totalUsed,
      trialStartDate: trialData.startDate,
      todayDate: today,
    };
  }, [trialData]);

  const consumeMessage = useCallback((): boolean => {
    const today = getTodayStr();
    const current = trialData ?? (() => {
      const d: TrialData = { startDate: today, dailyUsage: {} };
      return d;
    })();

    const dayNumber = getDayNumber(current.startDate);
    if (dayNumber > TRIAL_DAYS) return false;

    const usedToday = current.dailyUsage[today] ?? 0;
    if (usedToday >= MESSAGES_PER_DAY) return false;

    const updated: TrialData = {
      ...current,
      dailyUsage: {
        ...current.dailyUsage,
        [today]: usedToday + 1,
      },
    };
    saveTrialData(updated);
    setTrialData(updated);
    return true;
  }, [trialData]);

  const canSendMessage = useCallback((): boolean => {
    if (!trialData) return false;
    const today = getTodayStr();
    const dayNumber = getDayNumber(trialData.startDate);
    if (dayNumber > TRIAL_DAYS) return false;
    const usedToday = trialData.dailyUsage[today] ?? 0;
    return usedToday < MESSAGES_PER_DAY;
  }, [trialData]);

  const status = getStatus();

  return {
    trialData,
    status,
    initTrial,
    consumeMessage,
    canSendMessage,
    getStatus,
  };
}
